import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Car lockout",
  "Residential lockout",
  "Key fob programming",
  "Transponder / smart key",
  "Broken key extraction",
  "Spare / duplicate key",
  "Something else",
];

const inputClass =
  "h-12 rounded-xl border-zinc-300 bg-white/80 focus-visible:ring-2 focus-visible:ring-blue-500";

export const QuoteForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", vehicle: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Please choose a service.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request sent — we'll call you back within minutes.");
      setForm({ name: "", phone: "", vehicle: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call or text us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="relative py-24 md:py-32 overflow-hidden">
      <div
        className="absolute inset-0 bg-[linear-gradient(to_right,#e4e4e7_1px,transparent_1px),linear-gradient(to_bottom,#e4e4e7_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_60%,transparent_100%)]"
        aria-hidden="true"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 mb-4">Get a quote</p>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-zinc-950">
            Tell us what happened. <span className="text-brand">We'll take it from here.</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-zinc-600 max-w-md">
            Send the form and we call you back with an exact price before we
            roll. No surprises, no after-hours markup games.
          </p>
          <ul className="mt-8 space-y-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
            <li data-testid="quote-point-1">— Exact price before dispatch</li>
            <li data-testid="quote-point-2">— Licensed & insured technicians</li>
            <li data-testid="quote-point-3">— Dealership-level equipment</li>
          </ul>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ type: "spring", stiffness: 100, damping: 20, delay: 0.1 }}
          data-testid="quote-form"
          className="lg:col-span-7 rounded-3xl bg-white/70 backdrop-blur-xl border border-white/50 shadow-[0_8px_32px_rgba(0,0,0,0.06)] p-6 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
        >
          <div className="space-y-2">
            <label htmlFor="quote-name" className="text-sm font-medium text-zinc-700">Name</label>
            <Input
              id="quote-name"
              data-testid="quote-name-input"
              required
              placeholder="Your name"
              value={form.name}
              onChange={set("name")}
              className={inputClass}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="quote-phone" className="text-sm font-medium text-zinc-700">Phone</label>
            <Input
              id="quote-phone"
              data-testid="quote-phone-input"
              required
              type="tel"
              placeholder="(204) 000-0000"
              value={form.phone}
              onChange={set("phone")}
              className={inputClass}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="quote-vehicle" className="text-sm font-medium text-zinc-700">Vehicle</label>
            <Input
              id="quote-vehicle"
              data-testid="quote-vehicle-input"
              placeholder="e.g. 2019 Honda Civic"
              value={form.vehicle}
              onChange={set("vehicle")}
              className={inputClass}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-zinc-700">Service needed</label>
            <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
              <SelectTrigger data-testid="quote-service-select" className={inputClass}>
                <SelectValue placeholder="Choose a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`quote-service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <label htmlFor="quote-message" className="text-sm font-medium text-zinc-700">What happened?</label>
            <Textarea
              id="quote-message"
              data-testid="quote-message-input"
              placeholder="Locked out at Polo Park, keys visible on the seat..."
              value={form.message}
              onChange={set("message")}
              className="min-h-[120px] rounded-xl border-zinc-300 bg-white/80 focus-visible:ring-2 focus-visible:ring-blue-500"
            />
          </div>
          <div className="sm:col-span-2 flex flex-wrap items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={sending}
              data-testid="quote-submit-button"
              className="inline-flex items-center gap-2.5 rounded-full bg-brand px-8 py-4 text-base font-semibold text-white shadow-[0_0_20px_rgba(0,85,255,0.3)] hover:bg-brand-hover hover:-translate-y-0.5 transition-[background-color,transform] duration-200 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              {sending ? "Sending..." : "Request my quote"}
            </button>
            <p className="text-xs text-zinc-500">We reply by phone — usually within minutes.</p>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
